import React from "react";
import { action } from "store/Score";
import { connect } from "react-redux";
import styled from "styled-components";
import { Table, InputNumber, Tag, Button, Popconfirm, message } from "antd";
import { FullscreenOutlined, FullscreenExitOutlined, CheckOutlined } from "@ant-design/icons";

import { sum } from "utils/arr";
import { buildSuggestMap, getSuggestScore } from "utils/suggest";

const StyledSheet = styled.div`
  .factor-name {
    font-weight: bold;
    white-space: nowrap;
  }
  .not-fill {
    color: #fa541c;
    font-size: 12px;
    margin-left: 4px;
  }
  .factor-name-cell {
    text-align: left;
  }
  .factor-range {
    font-size: 12px;
    color: #bfbfbf;
    margin-top: 2px;
    white-space: nowrap;
    font-weight: bold;
  }
  .total-ok {
    color: #52c41a;
    font-weight: 600;
  }
  .total-bad {
    color: #ff4d4f;
    font-weight: 600;
  }
  .ant-table-tbody > tr > td {
    vertical-align: middle !important;
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: transparent !important;
  }
  /* 偶数行背景灰色；合计/小计列不处理背景 */
  .ant-table-tbody > tr.ant-table-row.even-row > td {
    background: #fafafa !important;
  }
  .ant-table-tbody > tr.ant-table-row.even-row > td.summary-cell {
    background: transparent !important;
  }
  .ant-table-tbody > tr.ant-table-row.even-row:hover > td {
    background: #fafafa !important;
  }
  .ant-table-tbody > tr.ant-table-row.even-row:hover > td.summary-cell {
    background: transparent !important;
  }
  .ant-table.ant-table-small tbody > tr > td{
    padding: 1px 8px;
  }
  .ant-input-number-input {
    height: 22px;
  }
  &:fullscreen {
    background: #fff;
    padding: 16px;
    overflow: auto;
    display: flex;
    flex-direction: column;
  }
  .fullscreen-bar {
    margin-bottom: 8px;
    display: flex;
    justify-content: flex-end;
  }
  .fullscreen-actions {
    display: flex;
    gap: 8px;
  }
  &:fullscreen {
    background: #fff;
    padding: 16px;
    overflow: auto;
    .fullscreen-submit {
      position: fixed;
      right: 2em;
      bottom: 1.5em;
      z-index: 999;
      box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
      0px 6px 10px 0px rgba(0, 0, 0, 0.14),
      0px 1px 18px 0px rgba(0, 0, 0, 0.12);
      border-radius: 40px;
    }
  }

  /* 红色高亮：被系统自动调整过的输入框 */
  .auto-adjusted .ant-input-number {
    border-color: #ff4d4f !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
  }

  /* 锁定标记 */
  .locked-badge {
    margin-left: 3px;
    font-size: 11px;
    color: #faad14;
  }
`;

const LEVEL_CONFIG = [
  { level: "basic", title: "基本因素层" },
  { level: "normal", title: "派生因素层" },
  { level: "sub", title: "因子层" }
];

const buildRows = (table) => {
  const basicList = table.get("basic") || [];
  const normalList = table.get("normal") || [];
  const subList = table.get("sub") || [];
  const rows = [];
  basicList.forEach((basic, bIdx) => {
    const normals = normalList.filter(
      (n) => n.get("parent_id") === basic.get("id")
    );
    const normalRows = normals.map((n) => {
      const subs = subList.filter((s) => s.get("parent_id") === n.get("id"));
      const normalSpan = 1 + subs.size;
      return { n, subs, normalSpan };
    });
    const groupSpan = 1 + normalRows.reduce((acc, { normalSpan }) => acc + normalSpan, 0);
    rows.push({ level: "basic", factor: basic, groupSpan, groupIndex: bIdx });
    normalRows.forEach(({ n, subs, normalSpan }, idx) => {
      rows.push({
        level: "normal",
        factor: n,
        normalSpan,
        firstInBlock: idx === 0,
        blockSpan: groupSpan,
        groupIndex: bIdx
      });
      subs.forEach((s, sIdx) => {
        rows.push({
          level: "sub",
          factor: s,
          firstInNormal: sIdx === 0,
          subBlockSpan: normalSpan,
          groupIndex: bIdx
        });
      });
    });
  });
  return rows;
};

const COL_LEVEL = {
  "basic-name": "basic",
  "basic-score": "basic",
  "basic-suggest": "basic",
  "normal-name": "normal",
  "normal-score": "normal",
  "normal-suggest": "normal",
  "sub-name": "sub",
  "sub-score": "sub",
  "sub-suggest": "sub"
};

const TotalValue = ({ values }) => {
  if (!values.length) return <span className="suggest">—</span>;
  const fillable = values
    .filter((v) => v != null)
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
  const total = parseInt(sum(fillable));
  const hasEmpty = values.length > fillable.length;
  const ok = total === 100 && !hasEmpty;
  return (
    <span>
      <span className={ok ? "total-ok" : "total-bad"}>{total}</span>
      {ok ? (
        <Tag style={okTagStyle}>校验通过</Tag>
      ) : (
        <Tag style={badTagStyle}>校验未通过</Tag>
      )}
    </span>
  );
};

const okTagStyle = {
  marginLeft: 4,
  color: "#52c41a",
  background: "#f6ffed",
  borderColor: "#b7eb8f"
};
const badTagStyle = {
  marginLeft: 4,
  color: "#ff4d4f",
  background: "#fff1f0",
  borderColor: "#ffa39e"
};

const SummaryCell = ({ table, childLevel, parentId }) => {
  const values = (table.get(childLevel) || [])
    .filter((f) => f.get("parent_id") === parentId && f.get("status", 1) !== 0)
    .map((f) => f.get("value"))
    .toArray();
  return <TotalValue values={values} />;
};

const BasicTotalCell = ({ table }) => {
  const values = (table.get("basic") || [])
    .filter((f) => f.get("status", 1) !== 0)
    .map((f) => f.get("value"))
    .toArray();
  return <TotalValue values={values} />;
};

const fmt1 = (v) => {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

class ScoreInput extends React.Component {
  render() {
    const { factor, level, tableId, tableValueChange } = this.props;
    const isSingle = factor.get("type", 1) === 1;
    const min = isSingle
      ? 0
      : factor.get("minimum") == null
      ? 0
      : factor.get("minimum");
    const max = isSingle
      ? 100
      : factor.get("maximum") == null
      ? 100
      : factor.get("maximum");
    const disabled = factor.get("status", 1) !== 1;
    const value = factor.get("value");
    const isAutoAdjusted = factor.get("autoAdjusted");
    const wrapperClass = isAutoAdjusted ? "auto-adjusted" : "";
    
    return (
      <>{
        disabled ? '—' :(
          <div className={wrapperClass} style={{ display: "inline-block" }}>
            <InputNumber
              min={min}
              max={max}
              step={1}
              value={value}
              precision={0}
              style={{ maxWidth: 120 }}
              placeholder="请输入"
              onChange={(v) => {
                tableValueChange(tableId, level, factor.get("id"), v);
              }}
            />
          </div>
        )
      }</>
    );
  }
}

class ScoreTableSheet extends React.Component {
  state = {
    innerHeight: typeof window !== "undefined" ? window.innerHeight : 800,
    isFullscreen: false
  };
  sheetRef = React.createRef();
  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    message.config({ getContainer: () => this.sheetRef.current || document.body });
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange
    );
  }
  handleResize = () => {
    this.setState({ innerHeight: window.innerHeight });
  };
  handleFullscreenChange = () => {
    this.setState({ isFullscreen: !!document.fullscreenElement });
  };
  toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (
      this.sheetRef.current &&
      this.sheetRef.current.requestFullscreen
    ) {
      this.sheetRef.current.requestFullscreen();
    }
  };

  render() {
    const { tableInfo, tableId, tableValueChange, rounds, handleSubmit } = this.props;
    const { isFullscreen, innerHeight } = this.state;
    const bodyHeight = Math.max(240, innerHeight - (isFullscreen ? 100 : 348));
    if (!tableInfo) return null;
    const suggestMap = buildSuggestMap(tableInfo, rounds);
    const levels = LEVEL_CONFIG;
    const rows = buildRows(tableInfo);
    const hasRows = (lvl) => rows.some((r) => r.level === lvl);
    
    const PLACEHOLDER = () => ({
      children: <span className="suggest">—</span>,
      props: { rowSpan: 1 }
    });
    const HIDDEN = { children: null, props: { colSpan: 0, rowSpan: 0 } };
    const isEmptyLevelCell = (row, colLevel) =>
      row.level !== colLevel && !hasRows(colLevel);
    
    const cellRowSpan = (colKey) => (row) => {
      const colLevel = COL_LEVEL[colKey];
      if (row.level === colLevel) {
        if (row.level === "basic") return { rowSpan: row.groupSpan };
        if (row.level === "normal") return { rowSpan: row.normalSpan };
        return { rowSpan: 1 };
      }

      if (row.level === "normal" && colLevel === "sub" && row.normalSpan === 1) {
        return { rowSpan: 1 };
      }
      if (isEmptyLevelCell(row, colLevel)) return { rowSpan: 1 };
      return { rowSpan: 0 };
    };
    
    const renderFactorName = (factor) => {
      const isSingle = factor.get("type", 1) === 1;
      const min = isSingle
        ? 0
        : factor.get("minimum") == null
        ? 0
        : factor.get("minimum");
      const max = isSingle
        ? 100
        : factor.get("maximum") == null
        ? 100
        : factor.get("maximum");
      return (
        <div className="factor-name-cell">
          <span className="factor-name">
            {factor.get("name")}
            {factor.get("status", 1) === 0 ? (
              <span className="not-fill">（不填）</span>
            ) : null}
          </span>
          <span className="factor-range">（{min}~{max}）</span>
        </div>
      );
    };
    const summaryCell = (lvl, row) => {
      const summary = (childLevel, rowSpan) => ({
        children: (
          <SummaryCell
            table={tableInfo}
            childLevel={childLevel}
            parentId={row.factor.get("parent_id")}
          />
        ),
        props: { rowSpan }
      });

      if (lvl === "normal") {
        if (row.level === "normal" && row.firstInBlock)
          return summary("normal", row.blockSpan - 1);
        return hasRows(lvl) ? HIDDEN : PLACEHOLDER();
      }
      if (row.level === "sub" && row.firstInNormal)
        return summary("sub", row.subBlockSpan - 1);
      if (row.level === "normal" && row.normalSpan === 1) return PLACEHOLDER();
      return hasRows(lvl) ? HIDDEN : PLACEHOLDER();
    };

    const columns = [];
    levels.forEach(({ level, title }) => {
      // name / suggest / score 列属于同一层级
      ["name", "suggest", "score"].forEach((colType) => {
        const colKey = `${level}-${colType}`;

        const makeRender = () => (_, row) => {
          const colLevel = COL_LEVEL[colKey];
          if (row.level === "normal" && row.normalSpan === 1 && colLevel === "sub") {
            return PLACEHOLDER();
          }
          
          if (row.level !== colLevel) {
            return isEmptyLevelCell(row, colLevel) ? PLACEHOLDER() : HIDDEN;
          }

          let span = 1;
          if (row.level === "basic") span = row.groupSpan;
          else if (row.level === "normal") span = row.normalSpan;

          let content = null;
          if (row.level === "basic" || row.level === "normal" || row.level === "sub") {
            if (colType === "name") {
              content = renderFactorName(row.factor);
            } else if (colType === "score") {
              content = (
                <ScoreInput
                  factor={row.factor}
                  level={row.level}
                  tableId={tableId}
                  tableValueChange={tableValueChange}
                />
              );
            } else {
              // 建议分值：第 1 轮用 binding 的 suggest_score；
              // 第 2 轮及以后用上一轮均值（保留 1 位小数）
              const suggestScore = getSuggestScore(
                row.factor,
                rounds,
                suggestMap,
                row.level
              );
              content = (
                <span className="suggest">
                  {suggestScore == null ? "—" : fmt1(suggestScore)}
                </span>
              );
            }
          }

          return { children: content, props: { rowSpan: span } };
        };

        columns.push({
          title:
            colType === "name"
              ? title
              : colType === "score" ? `专家权重打分` : rounds === 1 ? "建议分值" : "第一轮均值",
          dataIndex: colKey,
          key: colKey,
          width: colType === "name" ? 130 : colType === "score" ? 100 : 65,
          align: colType === "name" ? "left" : "center",
          onCell: cellRowSpan(colKey),
          render: makeRender()
        });
      });
      if (level === "basic") {
        columns.push({
          title: "合计",
          dataIndex: "basic-total",
          key: "basic-total",
          className: "summary-cell",
          width: 85,
          align: "center",
          onCell: (_, index) => ({
            rowSpan: index === 0 ? rows.length : 0
          }),
          render: (_, __, index) =>
            index === 0
              ? {
                  children: <BasicTotalCell table={tableInfo} />,
                  props: { rowSpan: rows.length }
                }
              : { children: null, props: { colSpan: 0, rowSpan: 0 } }
        });
      }
      if (level === "normal") {
        columns.push({
          title: "小计",
          dataIndex: "normal-total",
          key: "normal-total",
          width: 85,
          align: "center",
          onCell: (row) => summaryCell("normal", row).props,
          render: (_, row) => summaryCell("normal", row)
        });
      }
      if (level === "sub") {
        columns.push({
          title: "小计",
          dataIndex: "sub-total",
          key: "sub-total",
          width: 85,
          align: "center",
          onCell: (row) => summaryCell("sub", row).props,
          render: (_, row) => summaryCell("sub", row)
        });
      }
    });

    const tableWidth = columns.reduce((acc, c) => acc + (c.width || 0), 0);

    return (
      <StyledSheet ref={this.sheetRef}>
        <div className="fullscreen-bar">
          <div className="fullscreen-actions">
            <Button
              size="small"
              icon={
                this.state.isFullscreen ? (
                  <FullscreenExitOutlined />
                ) : (
                  <FullscreenOutlined />
                )
              }
              onClick={this.toggleFullscreen}
            >
              {this.state.isFullscreen ? "退出全屏" : "全屏"}
            </Button>
          </div>
          {this.state.isFullscreen && (
            <div className="fullscreen-submit">
              <Popconfirm
                title="提交评分？"
                onConfirm={handleSubmit}
                getPopupContainer={() => this.sheetRef.current}
                okText="确定"
                cancelText="再看看"
              >
                <Button
                    type="primary"
                    shape="round"
                    size="large"
                    icon={<CheckOutlined />}
                >
                  提交
                </Button>
              </Popconfirm>
            </div>
          )}
        </div>
        <Table
          rowKey={(row, idx) => {
            // 所有行（basic/normal/sub）都带 factor，id 唯一
            return `${row.level}-${row.factor.get("id")}`;
          }}
          rowClassName={(row) =>
            (row.groupIndex || 0) % 2 === 1 ? "even-row" : ""
          }
          columns={columns}
          dataSource={rows}
          pagination={false}
          size="small"
          bordered
          tableLayout="fixed"
          scroll={{ x: tableWidth, y: bodyHeight }}
        />
      </StyledSheet>
    );
  }
}

const STP = (state, ownProps) => ({
  tableInfo: state.getIn(["score", "tablesMap", ownProps.tableId])
});

const DTP = (dispatch) => ({
  tableValueChange: (tableId, level, factorId, value) => {
    dispatch(action.tableValueChange(tableId, level, factorId, value));
  }
});

export default connect(STP, DTP)(ScoreTableSheet);
